import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

type Theme = 'light' | 'dark' | 'system';
type ResolvedTheme = 'light' | 'dark';

interface ThemeContextValue {
  theme: Theme;
  resolvedTheme: ResolvedTheme;
  setTheme: (theme: Theme) => void;
}

const STORAGE_KEY = 'voxcore-theme';

function getSystemTheme(): ResolvedTheme {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
}

function resolveTheme(theme: Theme): ResolvedTheme {
  if (theme === 'system') return getSystemTheme();
  return theme;
}

function getEmbedTheme(): ResolvedTheme | null {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('embed') === 'true') {
      const t = params.get('theme');
      if (t === 'dark' || t === 'light') return t;
    }
  } catch { /* */ }
  return null;
}

function getStoredTheme(): Theme {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') {
      return stored;
    }
  } catch {
    // localStorage may not be available
  }
  return 'light'; // default to light
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: 'light',
  resolvedTheme: 'light',
  setTheme: () => {},
});

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [embedThemeLock] = useState<ResolvedTheme | null>(getEmbedTheme);
  const [theme, setThemeState] = useState<Theme>(embedThemeLock || getStoredTheme);
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(embedThemeLock || resolveTheme(getStoredTheme()));

  const setTheme = useCallback((newTheme: Theme) => {
    setThemeState(newTheme);
    try {
      localStorage.setItem(STORAGE_KEY, newTheme);
    } catch {
      // ignore
    }
  }, []);

  // Apply theme to document body (embed theme locks override user/system pref)
  useEffect(() => {
    if (embedThemeLock) {
      document.body.dataset.theme = embedThemeLock;
      return;
    }
    const resolved = resolveTheme(theme);
    setResolvedTheme(resolved);
    document.body.dataset.theme = resolved;
  }, [theme, embedThemeLock]);

  // Same-origin frames (the sandbox rail's /b/{id} iframe) each mount their
  // own provider and read localStorage once; the browser fires `storage` in
  // every OTHER frame on a write, so follow it and the iframe re-themes with
  // the shell instead of keeping the theme it loaded with.
  useEffect(() => {
    if (embedThemeLock) return;
    const handler = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return;
      const v = e.newValue;
      if (v === 'light' || v === 'dark' || v === 'system') setThemeState(v);
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, [embedThemeLock]);

  // Listen for system preference changes when theme is 'system'
  useEffect(() => {
    if (theme !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => {
      const resolved = e.matches ? 'dark' : 'light';
      setResolvedTheme(resolved);
      document.body.dataset.theme = resolved;
    };

    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, resolvedTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
